import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToCurrentWeek,
  subscribeToWeekTransactions,
  getWeekTransactions,
  subscribeToWeeks,
  closeOutWeek,
  convertPlannedToActual,
  updateTransaction,
  recalculateWeekTotals,
  recalculateWeekTotalsById,
  getCurrentWeekId,
  getNextWeekId,
  getOrCreateNextWeek,
  formatCurrency,
  calculateMatch,
  getActiveGoal,
  applyGoalAllocation,
  weeksUntilTarget,
} from '../../services/firebase';

const CATEGORY_EMOJI = {
  food: '🍕', transport: '🚗', groceries: '🛒', entertainment: '🎬',
  clothing: '👕', health: '💊', books: '📚', other: '📦',
  gift: '🎁', job: '💼', refund: '↩️',
};

const STEPS = ['planned', 'review', 'savings', 'goal', 'confirm', 'done'];

const StudentCloseOut = () => {
  const { family } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Support closing a prior week passed via navigation state
  const priorWeek = location.state?.week || null;
  const priorWeekId = location.state?.weekId || null;
  const isPriorWeek = !!priorWeekId;

  const [currentWeek, setCurrentWeek] = useState(priorWeek || null);
  const [transactions, setTransactions] = useState([]);
  const [savedAmount, setSavedAmount] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [activeGoal, setActiveGoal] = useState(null);
  const [goalJustCompleted, setGoalJustCompleted] = useState(null); // goal object if completed this close-out
  const [step, setStep] = useState(null); // determined after loading
  const [plannedResolutions, setPlannedResolutions] = useState({}); // txId -> 'spent' | 'delete' | 'rollover'
  const [spentAmounts, setSpentAmounts] = useState({}); // txId -> string amount
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolvedSpent, setResolvedSpent] = useState(null); // actual spent after planned resolutions applied
  const [priorClosedWeeks, setPriorClosedWeeks] = useState([]);

  // Declare these before any effects that reference them
  const matchConfig = family?.match;
  const weekId = isPriorWeek ? priorWeekId : getCurrentWeekId();

  // Compute effective weekly target from remaining balance, same as GoalCard
  // This corrects stale weeklyTarget values on older goal docs
  const effectiveGoalWeeklyTarget = (() => {
    if (!activeGoal) return 0;
    const targetDate = activeGoal.targetDate?.toDate
      ? activeGoal.targetDate.toDate()
      : activeGoal.targetDate?.seconds
        ? new Date(activeGoal.targetDate.seconds * 1000)
        : null;
    // Count weeks from the week being closed — NOT "now". On a late close-out (a new week
    // has already started), "now" sits in the next week and would count one fewer week,
    // inflating the target. For a prior-week close-out, anchor to that prior week so the
    // target matches what it was as of that week (e.g. 12 weeks → $23.34, not 11 → $25.46).
    const nowForWeeks = new Date();
    const anchorDate = isPriorWeek
      ? new Date(nowForWeeks.getFullYear(), nowForWeeks.getMonth(), nowForWeeks.getDate() - 7)
      : nowForWeeks;
    const weeksLeft = weeksUntilTarget(targetDate, anchorDate);
    const remaining = Math.max(0, (activeGoal.targetAmount || 0) - (activeGoal.amountSaved || 0));
    if (weeksLeft && remaining > 0) {
      return Math.round(Math.ceil((remaining / weeksLeft) * 100) / 100 * 100) / 100;
    }
    return activeGoal.weeklyTarget || 0;
  })();

  // Fetch prior closed weeks for personalized close-out message
  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToWeeks(family.id, (weeks) => {
      setPriorClosedWeeks(weeks.filter(w => w.closedOut && w.id !== weekId).sort((a, b) => b.id.localeCompare(a.id)));
    });
    return unsub;
  }, [family?.id, weekId]);

  // Load active savings goal
  useEffect(() => {
    if (!family?.id) return;
    getActiveGoal(family.id).then(goal => {
      setActiveGoal(goal);
      // goalAmount pre-fill is handled by the savings step continue button
      // using effectiveGoalWeeklyTarget which accounts for remaining balance
    }).catch(console.error);
  }, [family?.id]);

  // Load week and transactions
  useEffect(() => {
    if (!family?.id) return;

    if (isPriorWeek) {
      // Load prior week transactions directly
      getWeekTransactions(family.id, weekId)
        .then(txs => {
          setTransactions(txs);
          setLoading(false);
        })
        .catch(console.error);
    } else {
      // Subscribe to current week
      const unsubWeek = subscribeToCurrentWeek(family.id, (week) => {
        setCurrentWeek(week);
      });
      const unsubTx = subscribeToWeekTransactions(family.id, weekId, (txs) => {
        setTransactions(txs);
        setLoading(false);
      });
      return () => { unsubWeek(); unsubTx(); };
    }
  }, [family?.id, weekId, isPriorWeek]);

  // Determine initial step once loaded
  useEffect(() => {
    if (loading || step !== null) return;
    const unresolved = transactions.filter(t => t.type === 'planned');
    setStep(unresolved.length > 0 ? 'planned' : 'review');
  }, [loading, transactions, step]);

  const plannedTxs = transactions.filter(t => t.type === 'planned');
  const actualTxs = transactions.filter(t => (t.type === 'actual' || !t.type) && t.type !== 'deleted' && t.type !== 'converted');
  // Use resolvedSpent after planned items are processed — avoids stale week doc causing wrong carryover
  const spent = resolvedSpent !== null
    ? resolvedSpent
    : currentWeek?.spent || actualTxs.reduce((s, t) => s + t.amount, 0);
  const bonusIncome = currentWeek?.bonusIncome || 0;
  const allowanceAmount = (currentWeek?.allowanceAmount || family?.allowance?.amount || 0) + bonusIncome;
  const maxSaveable = Math.max(0, allowanceAmount - spent);
  const savingsVal = parseFloat(savedAmount) || 0;
  // Compare in whole cents: float noise (e.g. 22.209999… vs 22.21) must not falsely block the cap
  const overCap = Math.round(savingsVal * 100) > Math.round(maxSaveable * 100);
  const matchEarned = calculateMatch(savingsVal, matchConfig);
  const carryover = Math.round((allowanceAmount - spent - savingsVal) * 100) / 100;

  // Week label for display
  const weekLabel = isPriorWeek
    ? `Week ${weekId?.split('-')[1]} (last week)`
    : 'This week';

  // ─── Planned item resolution handlers ──────────────────────────────────────

  const setResolution = (txId, resolution) => {
    setPlannedResolutions(prev => ({ ...prev, [txId]: resolution }));
  };

  const setSpentAmount = (txId, amount) => {
    setSpentAmounts(prev => ({ ...prev, [txId]: amount }));
  };

  const allResolved = plannedTxs.every(tx => plannedResolutions[tx.id]);

  const handleApplyResolutions = async () => {
    setSaving(true);
    try {
      for (const tx of plannedTxs) {
        const resolution = plannedResolutions[tx.id];
        if (resolution === 'spent') {
          const amount = parseFloat(spentAmounts[tx.id]) || tx.amount;
          await convertPlannedToActual(family.id, weekId, tx, amount, transactions, tx.spendDate);
        } else if (resolution === 'delete') {
          await updateTransaction(family.id, tx.id, { type: 'deleted' }, transactions);
        } else if (resolution === 'rollover') {
          // Roll into the correct target week:
          // - closing a prior week → target is current week
          // - closing current week → target is next week
          const rollTargetId = isPriorWeek ? getCurrentWeekId() : getNextWeekId();
          // Ensure the target week doc exists before writing to it
          if (!isPriorWeek) await getOrCreateNextWeek(family.id, family?.allowance?.amount || 0);
          await updateTransaction(family.id, tx.id, { weekId: rollTargetId, type: 'planned' }, transactions);
          // Recalculate plannedSpend on the target week (it just gained a new planned tx)
          const targetTxs = await getWeekTransactions(family.id, rollTargetId);
          await recalculateWeekTotals(family.id, rollTargetId, targetTxs);
        }
      }
      // Refresh transactions after resolutions applied
      const updated = await getWeekTransactions(family.id, weekId);
      setTransactions(updated);

      // Recalculate source week totals to clear any stale plannedSpend
      // (rollover moves transactions out but doesn't auto-update source week)
      await recalculateWeekTotals(family.id, weekId, updated);

      // Calculate the true spent amount from fresh transaction data
      // This avoids using a stale week doc value in the carryover calculation
      const freshActuals = updated.filter(t =>
        (t.type === 'actual' || !t.type) && t.type !== 'deleted' && t.type !== 'converted'
      );
      const freshSpent = freshActuals.reduce((s, t) => s + (t.amount || 0), 0);
      setResolvedSpent(Math.round(freshSpent * 100) / 100);

      setStep('review');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Final close-out ────────────────────────────────────────────────────────

  const handleClose = async () => {
    setSaving(true);
    try {
      // Always fetch fresh transactions immediately before closing out
      // to avoid stale subscription state causing wrong carryover calculation
      // (race condition: student may have logged spend seconds before close-out)
      const freshTxs = await getWeekTransactions(family.id, weekId);
      const freshActuals = freshTxs.filter(t =>
        (t.type === 'actual' || !t.type) && t.type !== 'deleted' && t.type !== 'converted'
      );
      const freshSpent = Math.round(freshActuals.reduce((s, t) => s + (t.amount || 0), 0) * 100) / 100;
      const freshCarryover = Math.round((allowanceAmount - freshSpent - savingsVal) * 100) / 100;

      // Cap the allocation at BOTH this week's savings AND the goal's remaining balance,
      // so amountSaved can never exceed targetAmount (over-allocation bug).
      const goalRemaining = activeGoal
        ? Math.max(0, (activeGoal.targetAmount || 0) - (activeGoal.amountSaved || 0))
        : 0;
      const goalAllocationVal = activeGoal
        ? Math.min(parseFloat(goalAmount) || 0, savingsVal, goalRemaining)
        : 0;
      await closeOutWeek(family.id, weekId, {
        saved: savingsVal,
        matchEarned,
        carryover: freshCarryover,
        goalAllocation: Math.round(goalAllocationVal * 100) / 100,
        goalName: activeGoal?.name || '',
      });
      if (activeGoal && goalAllocationVal > 0) {
        const goalResult = await applyGoalAllocation(family.id, goalAllocationVal, weekId);
        if (goalResult?.justCompleted) setGoalJustCompleted(goalResult);
      }
      setStep('done');
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  };

  // ─── Already closed ─────────────────────────────────────────────────────────

  if (!loading && currentWeek?.closedOut && !isPriorWeek) {
    return (
      <div className="screen screen--no-nav" style={{ display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2>This week is closed ✓</h2>
          <p style={{ color: 'var(--color-text-2)', marginTop: 12, lineHeight: 1.6 }}>
            Great job! Your savings are locked in. A fresh week starts next Sunday.
          </p>
          <button className="btn btn--primary" style={{ marginTop: 32 }}
            onClick={() => navigate('/')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (loading || step === null) {
    return (
      <div className="screen screen--no-nav" style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  // ─── Done screen ────────────────────────────────────────────────────────────

  if (step === 'done') {
    // Build personalized message based on close-out outcome
    const isFirstSavings = savingsVal > 0 && priorClosedWeeks.every(w => (w.saved || 0) === 0);
    const lastWeek = priorClosedWeeks[0];
    const isBestWeek = savingsVal > 0 && priorClosedWeeks.every(w => savingsVal >= (w.saved || 0));
    const streakWeeks = [{ saved: savingsVal }, ...priorClosedWeeks];
    // Count actual consecutive weeks with savings, starting from the current week
    let streakCount = 0;
    for (const w of streakWeeks) {
      if ((w.saved || 0) > 0) streakCount++;
      else break;
    }
    const hasSavingsStreak = streakCount >= 3;
    const isImproving = savingsVal > 0 && lastWeek && savingsVal > (lastWeek.saved || 0) && spent < (lastWeek.spent || Infinity);
    const isOverBudgetClose = carryover < 0;

    const getPersonalizedMessage = () => {
      if (isOverBudgetClose) return {
        emoji: null,
        headline: isPriorWeek ? 'Last week closed!' : 'Week closed!',
        message: `Tough week — you went ${formatCurrency(Math.abs(carryover))} over budget, which carries into next week. Fresh start Monday.`,
        color: 'var(--color-text-2)',
      };
      if (isFirstSavings) return {
        emoji: '🌱',
        headline: 'You saved for the first time!',
        message: `${formatCurrency(savingsVal)} set aside. First savings is a big deal — it means the concept clicked. Keep it going next week.`,
        color: 'var(--color-primary)',
      };
      if (hasSavingsStreak) return {
        emoji: '🔥',
        headline: `${streakCount} weeks saving in a row!`,
        message: `${formatCurrency(savingsVal)} saved this week. The habit is real now — consistency is everything.`,
        color: 'var(--color-primary)',
      };
      if (isBestWeek && priorClosedWeeks.length > 0) return {
        emoji: '📈',
        headline: 'Your best savings week yet!',
        message: `${formatCurrency(savingsVal)} saved — your personal best. See if you can match it next week.`,
        color: 'var(--color-primary)',
      };
      if (isImproving) return {
        emoji: '👏',
        headline: isPriorWeek ? 'Last week closed!' : 'Week closed!',
        message: `Spent less than last week and saved more. That's the trend you want to see.`,
        color: 'var(--color-primary)',
      };
      if (savingsVal > 0) return {
        emoji: '🎉',
        headline: isPriorWeek ? 'Last week closed!' : 'Week closed!',
        message: matchConfig?.enabled && matchEarned > 0
          ? `You saved ${formatCurrency(savingsVal)} and earned a ${formatCurrency(matchEarned)} match. Total to bank: ${formatCurrency(savingsVal + matchEarned)}.`
          : `You saved ${formatCurrency(savingsVal)} this week. Nice work.`,
        color: 'var(--color-primary)',
      };
      return {
        emoji: null,
        headline: isPriorWeek ? 'Last week closed!' : 'Week closed! 🎉',
        message: 'Your savings are locked in. A fresh week starts Monday.',
        color: 'var(--color-text-2)',
      };
    };

    const msg = getPersonalizedMessage();

    return (
      <div className="screen screen--no-nav" style={{ display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={checkCircle}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M8 20l8 8 16-16" stroke="white" strokeWidth="3"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {msg.emoji && (
            <div style={{ fontSize: 48, marginTop: 16 }}>{msg.emoji}</div>
          )}
          <h2 style={{ marginTop: msg.emoji ? 8 : 24, marginBottom: 12 }}>
            {msg.headline}
          </h2>
          <p style={{ color: msg.color, lineHeight: 1.6, marginBottom: 24 }}>
            {msg.message}
          </p>
          <div className="card" style={{ marginBottom: 32, textAlign: 'left' }}>
            <div className="stack stack-3">
              <ResultRow label="Spent" value={formatCurrency(spent)} />
              <ResultRow label="Saved" value={formatCurrency(savingsVal)} color="var(--color-primary)" />
              {matchConfig?.enabled && matchEarned > 0 && (
                <ResultRow label="Match earned" value={formatCurrency(matchEarned)} color="var(--color-primary)" />
              )}
              {matchConfig?.enabled && matchEarned > 0 && (
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                  <ResultRow label="Total to bank" value={formatCurrency(savingsVal + matchEarned)}
                    bold color="var(--color-primary)" />
                </div>
              )}
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                <ResultRow
                  label={carryover >= 0 ? 'Rolling to next week' : 'Overage next week'}
                  value={carryover >= 0 ? `+${formatCurrency(carryover)}` : formatCurrency(carryover)}
                  color={carryover >= 0 ? 'var(--color-primary)' : 'var(--color-error)'}
                  bold
                />
              </div>
            </div>
          </div>
          {/* Goal completion celebration */}
          {goalJustCompleted && (
            <div style={{
              background: 'var(--color-primary-pale)',
              border: '1.5px solid var(--color-primary-light)',
              borderRadius: 'var(--radius-lg)',
              padding: '16px',
              marginBottom: 24,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)',
                fontSize: '1rem', marginBottom: 6 }}>
                You hit your {goalJustCompleted.name} goal!
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-2)',
                lineHeight: 1.5, marginBottom: 12 }}>
                {formatCurrency(goalJustCompleted.targetAmount)} saved.
                Head to your Savings tab to mark it as purchased when you're ready.
              </div>
            </div>
          )}

          <button className="btn btn--primary btn--full btn--lg" onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen screen--no-nav">
      {/* Back button */}
      <button onClick={() => {
        if (step === 'planned') navigate('/');
        else if (step === 'review') {
          if (plannedTxs.length > 0) setStep('planned');
          else navigate('/');
        }
        else if (step === 'savings') setStep('review');
        else if (step === 'goal') setStep('savings');
        else if (step === 'confirm') activeGoal ? setStep('goal') : setStep('savings');
      }} style={backStyle}>← Back</button>

      {/* Prior week label */}
      {isPriorWeek && (
        <div className="badge badge--amber" style={{ marginBottom: 16, display: 'inline-flex' }}>
          ⏮ Closing {weekLabel}
        </div>
      )}

      {/* ── Step: Resolve planned items ───────────────────────────────────── */}
      {step === 'planned' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>Unconfirmed planned items</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            You have {plannedTxs.length} planned {plannedTxs.length === 1 ? 'item' : 'items'} that
            {plannedTxs.length === 1 ? "wasn't confirmed. What happened with it?" : "weren't confirmed. What happened with each one?"}
          </p>

          <div className="stack stack-4" style={{ marginBottom: 24 }}>
            {plannedTxs.map(tx => {
              const resolution = plannedResolutions[tx.id];
              const emoji = CATEGORY_EMOJI[tx.category] || '📦';
              return (
                <div key={tx.id} className="card">
                  {/* Transaction info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10,
                      background: 'var(--color-surface-2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                      {emoji}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{tx.note || tx.category}</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>
                        Planned {formatCurrency(tx.amount)}
                      </div>
                    </div>
                  </div>

                  {/* Resolution choices */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {[
                      { key: 'spent', label: '✓ Spent it', color: 'var(--color-error)', bg: 'var(--color-error-light)' },
                      { key: 'rollover', label: '→ Next week', color: 'var(--color-primary)', bg: 'var(--color-primary-pale)' },
                      { key: 'delete', label: '✕ Didn\'t spend', color: 'var(--color-text-2)', bg: 'var(--color-surface-2)' },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => setResolution(tx.id, opt.key)}
                        style={{
                          padding: '10px 6px', borderRadius: 'var(--radius-md)', border: '2px solid',
                          borderColor: resolution === opt.key ? opt.color : 'var(--color-border)',
                          background: resolution === opt.key ? opt.bg : 'white',
                          color: resolution === opt.key ? opt.color : 'var(--color-text-3)',
                          fontWeight: resolution === opt.key ? 700 : 500,
                          fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                          transition: 'all 150ms ease', lineHeight: 1.3,
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Amount input if "spent it" */}
                  {resolution === 'spent' && (
                    <div className="form-group animate-in" style={{ marginTop: 12 }}>
                      <label className="form-label">Actual amount spent</label>
                      <div className="input-with-prefix">
                        <span className="input-prefix">$</span>
                        <input className="form-input" type="number" min="0.01" step="0.01"
                          value={spentAmounts[tx.id] ?? tx.amount}
                          onChange={e => setSpentAmount(tx.id, e.target.value)}
                          style={{ fontSize: '1.1rem', fontFamily: 'var(--font-display)', fontWeight: 600 }} />
                      </div>
                      {spentAmounts[tx.id] && parseFloat(spentAmounts[tx.id]) !== tx.amount && (
                        <div className="form-hint">
                          {parseFloat(spentAmounts[tx.id]) > tx.amount
                            ? `${formatCurrency(parseFloat(spentAmounts[tx.id]) - tx.amount)} over plan`
                            : `${formatCurrency(tx.amount - parseFloat(spentAmounts[tx.id]))} under plan`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button className="btn btn--primary btn--full btn--lg"
            onClick={handleApplyResolutions}
            disabled={!allResolved || saving}>
            {saving
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : 'Continue →'}
          </button>
          <p style={{ textAlign: 'center', fontSize: '0.8125rem',
            color: 'var(--color-text-3)', marginTop: 12 }}>
            Select an option for each item above to continue
          </p>
        </div>
      )}

      {/* ── Step: Review ──────────────────────────────────────────────────── */}
      {step === 'review' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>Review {weekLabel}</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 28 }}>
            Here's what you spent. Next you'll decide how much to save.
          </p>

          <div className="card--hero" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ opacity: 0.75, fontSize: '0.875rem' }}>Budget</span>
              <span style={{ fontWeight: 700 }}>{formatCurrency(allowanceAmount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ opacity: 0.75, fontSize: '0.875rem' }}>Total spent</span>
              <span style={{ fontWeight: 700, color: '#FFDDD2' }}>{formatCurrency(spent)}</span>
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 16,
              display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ opacity: 0.85, fontWeight: 600 }}>Available to save</span>
              <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(maxSaveable)}</span>
            </div>
          </div>

          {actualTxs.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>
                {actualTxs.length} transactions
              </h3>
              <div className="stack stack-2">
                {actualTxs.map(tx => (
                  <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between',
                    fontSize: '0.875rem', padding: '8px 0',
                    borderBottom: '1px solid var(--color-border)' }}>
                    <span>{CATEGORY_EMOJI[tx.category] || '📦'} {tx.note || tx.category}</span>
                    <span style={{ fontWeight: 600 }}>-{formatCurrency(tx.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button className="btn btn--primary btn--full btn--lg"
            onClick={() => setStep('savings')} disabled={!currentWeek && !isPriorWeek}>
            Continue to savings →
          </button>
        </div>
      )}

      {/* ── Step: Savings ─────────────────────────────────────────────────── */}
      {step === 'savings' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>How much did you save?</h2>
          {maxSaveable <= 0 ? (
            <div style={{ background: '#FFF0F0', border: '1.5px solid #FFDDD2',
              borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 28 }}>
              <p style={{ color: 'var(--color-error)', fontWeight: 600, marginBottom: 4 }}>
                You spent more than your budget this week.
              </p>
              <p style={{ color: 'var(--color-error)', fontSize: '0.875rem', lineHeight: 1.5 }}>
                You spent {formatCurrency(spent)} against a {formatCurrency(allowanceAmount)} budget.
                The {formatCurrency(Math.abs(allowanceAmount - spent))} overage will be deducted from next week's budget.
                No savings this week — but next week is a fresh start.
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--color-text-2)', marginBottom: 28 }}>
              You have {formatCurrency(maxSaveable)} available to save from this week's budget.
            </p>
          )}

          {maxSaveable > 0 && (
          <div className="form-group" style={{ marginBottom: 24 }}>
            <label className="form-label">Savings amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0" max={maxSaveable} step="0.01"
                placeholder="0.00" value={savedAmount}
                onChange={e => setSavedAmount(e.target.value)}
                ref={el => { if (el) setTimeout(() => { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300); }}
                style={{ fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 700 }} />
            </div>
            {overCap && (
              <div className="form-error">You can save at most {formatCurrency(maxSaveable)}</div>
            )}
          </div>
          )}

          {/* Quick amounts */}
          {maxSaveable > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
            {[0, Math.round(maxSaveable * 0.25), Math.round(maxSaveable * 0.5), maxSaveable].map((amt, i) => (
              <button key={i} type="button" onClick={() => setSavedAmount(amt.toFixed(2))}
                style={{
                  padding: '10px 4px', borderRadius: 'var(--radius-md)', border: '1.5px solid',
                  borderColor: parseFloat(savedAmount) === amt ? 'var(--color-primary)' : 'var(--color-border)',
                  background: parseFloat(savedAmount) === amt ? 'var(--color-primary-pale)' : 'white',
                  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  color: parseFloat(savedAmount) === amt ? 'var(--color-primary)' : 'var(--color-text-2)',
                  fontFamily: 'var(--font-body)', transition: 'all 150ms ease',
                }}>
                {i === 0 ? '$0' : i === 3 ? 'All' : `${i * 25}%`}
              </button>
            ))}
          </div>
          )}

          {/* Match preview */}
          {matchConfig?.enabled && savingsVal > 0 && (
            <div className="card--accent animate-in" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--color-text-2)', fontSize: '0.875rem' }}>Your savings</span>
                <span style={{ fontWeight: 700 }}>{formatCurrency(savingsVal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--color-text-2)', fontSize: '0.875rem' }}>🎯 Parent match</span>
                <span style={{ fontWeight: 700, color: 'var(--color-primary)' }}>+{formatCurrency(matchEarned)}</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(45,106,79,0.2)', paddingTop: 8,
                display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, color: 'var(--color-primary)' }}>Total to bank</span>
                <span style={{ fontWeight: 700, color: 'var(--color-primary)',
                  fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>
                  {formatCurrency(savingsVal + matchEarned)}
                </span>
              </div>
            </div>
          )}

          <button className="btn btn--primary btn--full btn--lg"
            onClick={() => {
              // Show goal step for any close-out with an active goal and non-zero savings
              if (activeGoal && savingsVal > 0) {
                // Clamp pre-fill to what was actually saved
                const cap = Math.min(effectiveGoalWeeklyTarget, savingsVal);
                setGoalAmount(cap.toFixed(2));
                setStep('goal');
              } else {
                setStep('confirm');
              }
            }}
            disabled={overCap}>
            {activeGoal && savingsVal > 0 ? 'Continue to goal →' : 'Review & confirm →'}
          </button>
        </div>
      )}


      {/* ── Step: Goal allocation ─────────────────────────────────────────── */}
      {step === 'goal' && activeGoal && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>How much toward your goal?</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            You saved {formatCurrency(savingsVal)} this week. How much goes toward your{' '}
            <strong>{activeGoal.name}</strong> goal?
          </p>

          {/* Goal progress card */}
          <div className="card--accent" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.9rem' }}>
                ⭐ {activeGoal.name}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-primary)', fontWeight: 600 }}>
                {formatCurrency(activeGoal.amountSaved)} / {formatCurrency(activeGoal.targetAmount)}
              </div>
            </div>
            <div className="progress-bar" style={{ marginBottom: 6 }}>
              <div className="progress-bar__fill"
                style={{ width: `${Math.min(100, Math.round((activeGoal.amountSaved / activeGoal.targetAmount) * 100))}%` }} />
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', opacity: 0.85 }}>
              Weekly target: {formatCurrency(effectiveGoalWeeklyTarget)}
            </div>
          </div>

          {/* Amount input */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Amount toward {activeGoal.name}</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input className="form-input" type="number" min="0"
                max={savingsVal} step="0.01"
                value={goalAmount}
                onChange={e => setGoalAmount(e.target.value)}
                ref={el => { if (el) setTimeout(() => { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300); }}
                style={{ fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 700 }} />
            </div>
            {parseFloat(goalAmount) > savingsVal && (
              <div className="form-error">Can't exceed your savings of {formatCurrency(savingsVal)}</div>
            )}
            {parseFloat(goalAmount) > 0 && parseFloat(goalAmount) <= savingsVal && (
              <div className="form-hint">
                {formatCurrency(savingsVal - parseFloat(goalAmount))} goes to general savings
              </div>
            )}
          </div>

          {/* Quick amounts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8, marginBottom: 24 }}>
            {[
              { label: '$0', val: 0 },
              { label: formatCurrency(Math.min(effectiveGoalWeeklyTarget, savingsVal)), val: Math.min(effectiveGoalWeeklyTarget, savingsVal) },
              { label: 'All savings', val: savingsVal },
            ].map(({ label, val }) => (
              <button key={label} type="button"
                onClick={() => setGoalAmount(val.toFixed(2))}
                style={{
                  padding: '10px 4px', borderRadius: 'var(--radius-md)', border: '1.5px solid',
                  borderColor: Math.abs(parseFloat(goalAmount) - val) < 0.01
                    ? 'var(--color-primary)' : 'var(--color-border)',
                  background: Math.abs(parseFloat(goalAmount) - val) < 0.01
                    ? 'var(--color-primary-pale)' : 'white',
                  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  color: Math.abs(parseFloat(goalAmount) - val) < 0.01
                    ? 'var(--color-primary)' : 'var(--color-text-2)',
                  fontFamily: 'var(--font-body)', transition: 'all 150ms ease',
                }}>
                {label}
              </button>
            ))}
          </div>

          <button className="btn btn--primary btn--full btn--lg"
            onClick={() => setStep('confirm')}
            disabled={parseFloat(goalAmount) > savingsVal}>
            Review & confirm →
          </button>
        </div>
      )}

      {/* ── Step: Confirm ─────────────────────────────────────────────────── */}
      {step === 'confirm' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>Confirm close-out</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 28 }}>
            Once you close the week you can't make changes. Looks good?
          </p>

          <div className="card" style={{ marginBottom: 28 }}>
            <div className="stack stack-4">
              <ResultRow label="Budget" value={formatCurrency(allowanceAmount)} />
              <ResultRow label="Spent" value={formatCurrency(spent)} color="var(--color-accent)" />
              <ResultRow label="Saved" value={formatCurrency(savingsVal)} color="var(--color-primary)" />
              {matchConfig?.enabled && matchEarned > 0 && (
                <ResultRow label="🎯 Match earned" value={formatCurrency(matchEarned)} color="var(--color-primary)" />
              )}
              {activeGoal && parseFloat(goalAmount) > 0 && (
                <ResultRow label={`⭐ Toward ${activeGoal.name}`}
                  value={formatCurrency(parseFloat(goalAmount))}
                  color="var(--color-primary)" />
              )}
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                <ResultRow
                  label={carryover >= 0 ? 'Rolling to next week' : 'Overage next week'}
                  value={carryover >= 0 ? `+${formatCurrency(carryover)}` : formatCurrency(carryover)}
                  color={carryover >= 0 ? 'var(--color-primary)' : 'var(--color-error)'}
                  bold
                />
              </div>
            </div>
          </div>

          <button className="btn btn--primary btn--full btn--lg"
            onClick={handleClose} disabled={saving} style={{ marginBottom: 12 }}>
            {saving
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : 'Close Out Week ✓'}
          </button>
          <button className="btn btn--ghost btn--full" onClick={() => setStep('savings')}>
            ← Edit savings
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Shared sub-components ───────────────────────────────────────────────────

const ResultRow = ({ label, value, color, bold }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9375rem' }}>
    <span style={{ color: 'var(--color-text-2)' }}>{label}</span>
    <span style={{ fontWeight: bold ? 700 : 600, color: color || 'var(--color-text)' }}>{value}</span>
  </div>
);

const backStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-2)', marginBottom: 24, fontSize: '0.9rem',
  display: 'flex', alignItems: 'center', gap: 6, padding: 0,
  fontFamily: 'var(--font-body)',
};

const checkCircle = {
  width: 88, height: 88, borderRadius: '50%', background: 'var(--color-primary)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 8px 32px rgba(45,106,79,0.3)',
};

export default StudentCloseOut;
